import React, { useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	Modal,
	ModalBody,
	ModalFooter,
} from 'reactstrap';

import { FormField, FIELD_TYPES } from '@techchange/components';
import {
	Button,
	DangerAlert,
	LearnMoreModal,
	API_BASE_URL,
	getLanguageOptions,
	getAutoTranslateLanguageOptions,
} from '@techchange/components';
import { messages } from '../../i18n';

import './LanguageModal.scss';

const axiosClient = axios.create({
	withCredentials: true,
});

const languageOptions = getLanguageOptions({
	useNativeName: true,
});

const autoTranslateLanguageOptions = getAutoTranslateLanguageOptions({
	useNativeName: true,
});

const propTypes = {

	/**
	 * Data for the current user.
	 */
	currentUser: PropTypes.shape({

		/**
		 * The ID of the current user.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The user's language preference for platform UI.
		 */
		language: PropTypes.string,

		/**
		 * The user's language preference for automated translations.
		 */
		auto_translate_language: PropTypes.string,

		/**
		 * Flag indicating if auto-translations should be enabled by default
		 * for the user.
		 */
		auto_translate_default: PropTypes.bool,

	}).isRequired,

	/**
	 * Flag indicating if the modal is open.
	 */
	isOpen: PropTypes.bool.isRequired,

	/**
	 * Callback to toggle the modal open/closed.
	 */
	toggle: PropTypes.func.isRequired,

	/**
	 * NOTE: Temporary flag to hide fields related to automatic
	 * translation.
	 */
	hideTranslationFields: PropTypes.bool,

	/**
	 * Optional callback to invoke after a lngauge has been selected.
	 */
	onSelectLanguage: PropTypes.func,

};

/**
 * Modal that allows user to update their language preferences.
 *
 * A user can set two different language settings:
 *  1. Display Language: The user's language preference for UI translations.
 *  2. Automated Translation Language: The user's language preference for automated translations.
 *
 * A user can also configure automated translations to be enabled across the platform by default.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The component to render.
 */
const LanguageModal = (props) => {

	const { currentUser, isOpen, toggle, hideTranslationFields, onSelectLanguage } = props;

	const intl = useIntl();

	const [formData, setFormData] = useState({
		language: intl.locale,
		auto_translate_language: currentUser.auto_translate_language || intl.locale,
		auto_translate_default: currentUser.auto_translate_default,
	});

	const [showFormError, setShowFormError] = useState(false);

	/**
	 * Handles change to form inputs.
	 *
	 * @param {String} fieldName  The name of the changed field.
	 * @param {String} fieldValue  The updated value of the changed field.
	 */
	const handleOnChange = (fieldName, fieldValue) => {
		setFormData((prev) => ({
			...prev,
			[fieldName]: fieldValue,
		}));
	};

	/**
	 * Makes API request to save user preferences when submitting the form.
	 */
	const handleOnSubmit = () => {

		// Reset error state
		setShowFormError(false);

		axiosClient.patch(`${API_BASE_URL.USERS}/${currentUser.id}`, {
			...formData,
		}).then(() => {

			toggle();

			if (onSelectLanguage) {
				onSelectLanguage(formData);
			}

		}).catch((error) => {

			setShowFormError(true);
			console.error(error);

		});

	};

	return (
		<Modal
			className="tc-language-modal"
			isOpen={isOpen}
			toggle={toggle}>
			<ModalBody>
				{showFormError &&
					<DangerAlert>
						<FormattedMessage {...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_LANGUAGE_MODAL_ERROR_MSG_SAVE} />
					</DangerAlert>
				}

				<FormField
					type={FIELD_TYPES.SELECT}
					name="language"
					label={
						<span className="language-modal-form-label">
							{intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_LANGUAGE_MODAL_FORM_FIELD_DISPLAY_LANGUAGE_LABEL)}
						</span>
					}
					help={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_LANGUAGE_MODAL_FORM_FIELD_DISPLAY_LANGUAGE_HELP)}
					value={formData.language}
					options={languageOptions}
					intl={intl}
					isEditing
					onChange={handleOnChange} />
				{!hideTranslationFields &&
					<>
						<FormField
							type={FIELD_TYPES.SELECT}
							name="auto_translate_language"
							label={
								<div className="d-flex align-items-center">
									<span className="language-modal-form-label">
										<FormattedMessage
											{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_LANGUAGE_MODAL_FORM_FIELD_AUTO_TRANSLATE_LANGUAGE_LABEL} />
									</span>
									<LearnMoreModal
										id="language-modal-auto-translate-language-field-learn-more"
										className="ps-1"
										title={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_TEXT_LEARN_MORE)}
										header={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_LANGUAGE_MODAL_AUTO_TRANSLATE_LANGUAGE_LEARN_MORE_TITLE)}
										body={
											<>
												<p>
													<FormattedMessage
														{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_LANGUAGE_MODAL_AUTO_TRANSLATE_LANGUAGE_LEARN_MORE_BODY}
														values={{
															icon: (
																<>
																	<i
																		className="fas fa-language"
																		aria-hidden="true" />
																	<span className="fa-sr-only">
																		<FormattedMessage
																			{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_LANGUAGE_MODAL_AUTO_TRANSLATE_LANGUAGE_LEARN_MORE_TRANSLATION_ICON_ALT_TEXT} />
																	</span>
																</>
															),
														}} />
												</p>
												<hr />
												<p className="google-disclaimer">
													<FormattedMessage
														{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_LANGUAGE_MODAL_AUTO_TRANSLATE_LANGUAGE_LEARN_MORE_GOOGLE_TRANSLATE_DISCLAIMER} />
												</p>
											</>
										} />
								</div>
							}
							help={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_LANGUAGE_MODAL_FORM_FIELD_AUTO_TRANSLATE_LANGUAGE_HELP)}
							value={formData.auto_translate_language}
							options={autoTranslateLanguageOptions}
							intl={intl}
							isEditing
							onChange={handleOnChange} />
						<FormField
							className="default-toggle"
							type={FIELD_TYPES.SWITCH}
							name="auto_translate_default"
							label={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_LANGUAGE_MODAL_FORM_FIELD_AUTO_TRANSLATE_DEFAULT_LABEL)}
							value={Boolean(formData.auto_translate_default)}
							isEditing
							isLabelInline
							onChange={handleOnChange} />
					</>
				}
			</ModalBody>
			<ModalFooter>
				<Button
					color="primary"
					onClick={handleOnSubmit}>
					<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SAVE_LABEL} />
				</Button>
				<Button
					color="neutral"
					outline
					onClick={toggle}>
					<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL} />
				</Button>
			</ModalFooter>
		</Modal>
	);

};

LanguageModal.propTypes = propTypes;

export default LanguageModal;
